import { useCallback, useMemo } from 'react'

import {
  F0Box,
  SurveyFormBuilder,
  SurveyFormBuilderElement,
  QuestionType,
} from '@factorialco/f0-react'

import { useDirectUpload } from 'modules/core/hooks/useDirectUpload'

import {
  FromFormToQuestionnaire,
  FromQuestionnaireToForm,
} from 'modules/forms/components/newFormEditor/helpers'
import { QuestionnaireType } from 'modules/forms/components/newFormEditor/types'

const ALLOWED_QUESTION_TYPES: QuestionType[] = [
  'text',
  'longText',
  'select',
  'multi-select',
  'numeric',
  'link',
  'date',
  // TODO: uncomment as soon as the materialization/preview is ready for these types
  // 'file',
  // 'checkbox',
]

// The SurveyFormBuilder doesn't carry invariantId, so we need to keep track of them separately
function collectInvariantIds(elements: QuestionnaireType['elements']): Map<string, string> {
  const map = new Map<string, string>()
  for (const el of elements) {
    if (el.id && el.invariantId) map.set(el.id, el.invariantId)
    if ('elements' in el) {
      for (const child of el.elements) {
        if (child.id && child.invariantId) map.set(child.id, child.invariantId)
      }
    }
  }
  return map
}

interface Props {
  state: QuestionnaireType
  setState: (state: QuestionnaireType) => void
}

const FormEditor = ({ state, setState }: Props) => {
  const directUpload = useDirectUpload({ resourceType: 'Procurement::Type' })

  const useUpload = useCallback(
    () => ({
      upload: async (file: File) => {
        const result = await directUpload.upload(file)
        if (result.type === 'aborted') return { type: 'aborted' as const }
        return { type: 'success' as const, value: result.signedId }
      },
      cancelUpload: directUpload.cancelUpload,
      progress: directUpload.progress,
      status: directUpload.status,
    }),
    [directUpload]
  )

  const elements = useMemo(
    () =>
      FromQuestionnaireToForm(state.elements, {
        mapDropdownSingleTo: 'dropdown-single',
      }),
    [state.elements]
  )

  const invariantIds = useMemo(() => collectInvariantIds(state.elements), [state.elements])

  const handleChange = (updatedElements: SurveyFormBuilderElement[]) => {
    const converted = FromFormToQuestionnaire(updatedElements)

    for (const el of converted) {
      el.invariantId = invariantIds.get(el.id ?? '') ?? el.invariantId
      if ('elements' in el) {
        for (const child of el.elements) {
          child.invariantId = invariantIds.get(child.id ?? '') ?? child.invariantId
        }
      }
    }

    setState({ ...state, elements: converted })
  }

  return (
    <F0Box width="full" height="full" padding="md" paddingBottom="5xl">
      <SurveyFormBuilder
        elements={elements}
        onChange={handleChange}
        allowedQuestionTypes={ALLOWED_QUESTION_TYPES}
        useUpload={useUpload}
      />
    </F0Box>
  )
}

export default FormEditor
